import React, { useState, useEffect, useCallback } from "react";
import { FaEye, FaCheck } from "react-icons/fa";

import { Input, Select } from "@/components/Form";
import { Page, DashboardContainer, Row, Col } from "@/components/Grid";
import { AdminMenu } from "@/components/Admin";
import { Navbar } from "@/components/Blocks";
import Paginator, { Loading } from "@/components/Paginator";
import { Modal, Button } from "@/components/Common";
import { useTransacoes } from "@/hooks";
import toastify from "@/services/toastify";

import {
  Header,
  FiltroBasico,
  Totais,
  TransacaoListItem,
  ActionButton,
  StatusBadge,
  ModalContent,
  ModalBody,
  ModalFooter,
} from "./styles";

export default function Transferencias({ history }) {
  const { approveTransacao } = useTransacoes();
  const [transacoes, setTransacoes] = useState([]);
  const [readyToPaginate, setReadyToPaginate] = useState(false);
  const [query, setQuery] = useState({ filters: "transacoes,todos", status: "" });
  const [paginator, setPaginator] = useState(null);
  const [loadingTransacoes, setLoadingTransacoes] = useState(false);
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [transacaoToApprove, setTransacaoToApprove] = useState(null);
  const [approvingTransacao, setApprovingTransacao] = useState(false);

  function handleOnFetchComplete(data) {
    setTransacoes(data.data);
    setPaginator(data.paginator);
  }

  function handleOnChange(event) {
    const { name, value } = event.target;
    setQuery(state => {
      return { ...state, [name]: value };
    });
  }

  function renderHeader() {
    return (
      <Header>
        <h4>Financeiro</h4>
        <h2>Todas as transferências</h2>
        <div className="breadcrumbs">
          Financeiro {">"} <span>Todas as transferências</span>
        </div>
      </Header>
    );
  }

  function renderFiltroBasico() {
    const colNome = {
      lg: 0.7,
      md: 0.5,
      sm: 1,
    };

    const colStatus = {
      lg: 0.3,
      md: 0.5,
      sm: 1,
    };

    const statusOptions = [
      { id: '', title: 'Todos os status' },
      { id: 'DRAFT', title: 'Rascunho' },
      { id: 'INITIATED', title: 'Iniciada' },
      { id: 'PROCESSING', title: 'Processando' },
      { id: 'COMPLETED', title: 'Concluída' },
      { id: 'REFUNDED', title: 'Reembolsada' },
      { id: 'CANCELED', title: 'Cancelada' },
    ];

    return (
      <FiltroBasico>
        <Row hascols>
          <Col {...colNome}>
            <Input
              label="Nome do beneficiário"
              name="nome"
              placeholder="Busque pelo nome do beneficiário"
              onChange={handleOnChange}
            />
          </Col>
          <Col {...colStatus}>
            <Select
              label="Status"
              name="status"
              value={query.status || ""}
              defaultValue=" "
              options={statusOptions}
              onChange={handleOnChange}
            />
          </Col>
        </Row>
      </FiltroBasico>
    );
  }

  function renderTotais() {
    if (loadingTransacoes) return <></>;
    if (paginator !== null) {
      const { total } = paginator;
      if (total === 0) {
        return (
          <Totais>
            Sem transferências para exibir dentro dos critérios de busca
          </Totais>
        );
      }
      return (
        <Totais>
          Exibindo {transacoes.length} de {total} transferências
        </Totais>
      );
    }
  }

  function formatCurrency(value) {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100); // Dividindo por 100 porque o valor vem em centavos
  }

  function formatDate(dateString) {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  function getStatusColor(status) {
    switch (status) {
      case 'INITIATED': return '#f39c12';
      case 'PROCESSING': return '#3498db';
      case 'COMPLETED': return '#27ae60';
      case 'REFUNDED': return '#e74c3c';
      case 'CANCELED': return '#95a5a6';
      default: return '#95a5a6';
    }
  }

  function getStatusText(status) {
    switch (status) {
      case 'DRAFT': return 'Rascunho';
      case 'INITIATED': return 'Iniciada';
      case 'PROCESSING': return 'Processando';
      case 'COMPLETED': return 'Concluída';
      case 'REFUNDED': return 'Reembolsada';
      case 'CANCELED': return 'Cancelada';
      default: return status;
    }
  }


  function handleApproveTransacao(transacao) {
    setTransacaoToApprove(transacao);
    setOpenApproveModal(true);
  }

  function handleCloseApproveModal() {
    if (approvingTransacao) return; // Não permite fechar durante o loading
    setOpenApproveModal(false);
    setTransacaoToApprove(null);
  }

  async function confirmApproveTransacao() {
    if (!transacaoToApprove) return;

    setApprovingTransacao(true);

    const result = await approveTransacao(transacaoToApprove.id);

    if (result.success) {
      toastify("Transferência aprovada com sucesso!");
      setOpenApproveModal(false);
      setTransacaoToApprove(null);
      setApprovingTransacao(false);

      // Recarregar a página uma única vez
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      toastify(result.message || 'Erro ao aprovar transferência', "error");
      setApprovingTransacao(false);
    }
  }

  function renderTransacoes() {
    return transacoes.map(transacao => {
      return (
        <TransacaoListItem key={transacao.id}>
          <div className="coluna coluna-empresa">
            <h4>{transacao.aluno?.nome || transacao.franqueado?.nome}</h4>
            {transacao.status !== 'DRAFT' && (
              <p>Código: {transacao.code}</p>
            )}
          </div>
          <div className="coluna coluna-data">
            <h4>Data</h4>
            {formatDate(transacao.created_at)}
          </div>
          <div className="coluna coluna-valor">
            <h4>Valor</h4>
            {formatCurrency(transacao.amount)}
          </div>
          <div className="coluna coluna-status">
            <h4>Status</h4>
            <StatusBadge color={getStatusColor(transacao.status)}>
              {getStatusText(transacao.status)}
            </StatusBadge>
          </div>
          <div className="coluna coluna-actions">
            {transacao.status === 'DRAFT' && (
              <ActionButton onClick={() => handleApproveTransacao(transacao)}>
                <FaCheck color="#27ae60" size={14} /> <span>Aprovar</span>
              </ActionButton>
            )}
          </div>
        </TransacaoListItem>
      );
    });
  }

  const setQueryCB = useCallback(() => {
    setQuery(state => ({ ...state, filters: "transacoes,todos", status: "" }));
    setReadyToPaginate(true);
  }, []);

  useEffect(() => {
    setQueryCB();
  }, [setQueryCB]);

  return (
    <Page>
      <Navbar />
      <AdminMenu />
      <DashboardContainer>
        <Row>
          <Col lg={0.8} md={0.7} sm={1}>
            {renderHeader()}
            {renderFiltroBasico()}
            {renderTotais()}

            {loadingTransacoes ? (
              <Loading />
            ) : transacoes && transacoes.length > 0 ? (
              renderTransacoes()
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#6c7c7c' }}>
              </div>
            )}

            {readyToPaginate && (
              <Paginator
                endpoint="transacoes"
                query={query}
                onFetchComplete={handleOnFetchComplete}
                onLoading={l => setLoadingTransacoes(l)}
              />
            )}
          </Col>
        </Row>
      </DashboardContainer>

      {/* Modal de Aprovação */}
      <Modal open={openApproveModal}>
        <ModalContent>
          <ModalBody>
            <h3>Confirmar Aprovação</h3>
            <p>
              Tem certeza que deseja aprovar a transferência para{' '}
              <strong>{transacaoToApprove?.aluno?.nome || transacaoToApprove?.franqueado?.nome}</strong>?
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={handleCloseApproveModal}
              variant="cancel"
              disabled={approvingTransacao}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmApproveTransacao}
              variant="cyan"
              loading={approvingTransacao}
            >
              Aprovar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Page>
  );
}
